import React, { useState, useEffect } from 'react';
import reportApprovalService from '../../../services/reportApprovalService';
import doctorSignatureService from '../../../services/doctorSignatureService';
import PathologyReportPreviewModal from '../../pathology/components/PathologyReportPreviewModal';
import './ReportReviewModal.css';

export const ReportReviewModal = ({ testId, onClose, onActionSuccess }) => {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [releasing, setReleasing] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);
  const [isPrintPreviewOpen, setIsPrintPreviewOpen] = useState(false);

  // Form selections
  const [selectedDoctorId, setSelectedDoctorId] = useState('');
  const [selectedDoctorProfile, setSelectedDoctorProfile] = useState(null);
  const [releaseRemarks, setReleaseRemarks] = useState('');
  const [acknowledgeCritical, setAcknowledgeCritical] = useState(false);

  // Hold dialog state
  const [showHoldDialog, setShowHoldDialog] = useState(false);
  const [holdReason, setHoldReason] = useState('');
  const [holding, setHolding] = useState(false);

  // Confirmation step
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  useEffect(() => {
    const loadReview = async () => {
      try {
        setLoading(true);
        setErrorMsg(null);
        const res = await reportApprovalService.getReviewDetail(testId);
        const data = res.data;
        setDetail(data);

        // Pre-select doctor if already assigned, or default to first available
        if (data.reportingDoctorId && data.availableSignatures?.length > 0) {
          const match = data.availableSignatures.find(s => s.employeeId === data.reportingDoctorId);
          if (match) {
            setSelectedDoctorId(match.employeeId);
            setSelectedDoctorProfile(match);
          }
        } else if (data.availableSignatures?.length > 0) {
          setSelectedDoctorId(data.availableSignatures[0].employeeId);
          setSelectedDoctorProfile(data.availableSignatures[0]);
        }
      } catch (err) {
        setErrorMsg(err.response?.data?.message || 'Failed to load report review context.');
      } finally {
        setLoading(false);
      }
    };

    if (testId) {
      loadReview();
    }
  }, [testId]);

  const handleDoctorChange = (e) => {
    const docId = parseInt(e.target.value, 10);
    setSelectedDoctorId(docId);
    const match = detail?.availableSignatures?.find(s => s.employeeId === docId);
    setSelectedDoctorProfile(match || null);
  };

  const handleConfirmRelease = async () => {
    if (!selectedDoctorId) {
      alert('Please select an authorized reporting doctor.');
      return;
    }

    if (detail?.isCritical && !acknowledgeCritical) {
      alert('Please acknowledge review of the critical finding before releasing.');
      return;
    }

    try {
      setReleasing(true);
      await reportApprovalService.releaseReport(testId, {
        reportingDoctorId: selectedDoctorId,
        signatureProfileId: selectedDoctorProfile?.profileId,
        releaseRemarks: releaseRemarks || 'Clinically verified and released',
        acknowledgeCriticalFindings: acknowledgeCritical
      });

      setShowConfirmModal(false);
      onActionSuccess?.('Report approved and released successfully with doctor signature applied.');
      onClose();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to release report.');
    } finally {
      setReleasing(false);
    }
  };

  const handleConfirmHold = async () => {
    if (!holdReason.trim()) {
      alert('Please specify a reason for placing this report on hold.');
      return;
    }

    try {
      setHolding(true);
      await reportApprovalService.holdReport(testId, holdReason);
      setShowHoldDialog(false);
      onActionSuccess?.('Report placed on clinical hold.');
      onClose();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to place report on hold.');
    } finally {
      setHolding(false);
    }
  };

  if (!testId) return null;

  return (
    <div className="rep-rev-overlay">
      <div className="rep-rev-modal">
        {/* Header */}
        <div className="rep-rev-header">
          <div className="rep-rev-header-title">
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <h2>Clinical Report Review & Authorized Release</h2>
              {detail?.approved === 1 && (
                <span style={{
                  background: '#dcfce7',
                  color: '#15803d',
                  padding: '2px 8px',
                  borderRadius: '4px',
                  fontWeight: '700',
                  fontSize: '0.75rem',
                  border: '1px solid #86efac'
                }}>
                  ✓ OFFICIALLY RELEASED & LOCKED
                </span>
              )}
            </div>
            <div className="rep-rev-header-sub">
              Accession: <strong>{detail?.ledgerTransactionNo || '—'}</strong> | Department:{' '}
              <strong>{detail?.department || 'Pathology'}</strong> | Investigation:{' '}
              <strong>{detail?.itemName || '—'}</strong>
            </div>
          </div>
          <button type="button" className="rep-rev-close-btn" onClick={onClose}>
            ✕
          </button>
        </div>

        {loading ? (
          <div style={{ padding: '60px', textAlign: 'center', color: '#64748b' }}>
            Loading clinical report context and parameters...
          </div>
        ) : errorMsg ? (
          <div style={{ padding: '30px', color: '#ef4444', textAlign: 'center' }}>
            {errorMsg}
          </div>
        ) : detail && (
          <>
            {/* Patient Demographics Strip */}
            <div className="rep-rev-patient-strip">
              <div className="demo-cell">
                <label>Patient Name</label>
                <span>{detail.patientName}</span>
              </div>
              <div className="demo-cell">
                <label>UHID / Patient ID</label>
                <span>{detail.patientId}</span>
              </div>
              <div className="demo-cell">
                <label>Age / Gender</label>
                <span>{detail.ageGender}</span>
              </div>
              <div className="demo-cell">
                <label>Barcode / SIN</label>
                <span>{detail.barcodeNo || '—'}</span>
              </div>
              <div className="demo-cell">
                <label>Sample Received</label>
                <span>
                  {detail.sampleReceiveDate
                    ? new Date(detail.sampleReceiveDate).toLocaleString('en-IN', { dateStyle: 'short', timeStyle: 'short' })
                    : 'Recorded'}
                </span>
              </div>
              <div className="demo-cell">
                <label>Result Entered By</label>
                <span style={{ color: '#0369a1' }}>
                  {detail.resultEnteredBy ? `${detail.resultEnteredBy} (Technician)` : 'Pending Entry'}
                </span>
              </div>
            </div>

            {/* Critical Result Banner */}
            {detail.isCritical && (
              <div className="rep-rev-critical-banner">
                <span className="critical-icon">⚠️</span>
                <div style={{ flex: 1 }}>
                  <strong>CRITICAL / PANIC VALUE ALERT:</strong> One or more parameters in this test exceed critical safety thresholds.
                  Review requires clinical verification and explicit acknowledgment before report release.
                </div>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontWeight: '700' }}>
                  <input
                    type="checkbox"
                    checked={acknowledgeCritical}
                    onChange={(e) => setAcknowledgeCritical(e.target.checked)}
                  />
                  <span>Acknowledge Critical Review</span>
                </label>
              </div>
            )}

            {/* Body */}
            <div className="rep-rev-body">
              {detail.department !== 'Radiology' ? (
                <>
                  {/* Phase 6C: Clinical Decision Support Findings Panel */}
                  {detail.validationFindings && detail.validationFindings.length > 0 && (
                    <div style={{
                      marginBottom: '16px',
                      padding: '14px',
                      borderRadius: '8px',
                      border: detail.overallValidationSeverity === 'CRITICAL' ? '2px solid #ef4444' : '1px solid #f59e0b',
                      background: detail.overallValidationSeverity === 'CRITICAL' ? '#fef2f2' : '#fffbeb'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ fontSize: '1.2rem' }}>{detail.overallValidationSeverity === 'CRITICAL' ? '🚨' : '⚠️'}</span>
                          <strong style={{
                            color: detail.overallValidationSeverity === 'CRITICAL' ? '#991b1b' : '#92400e',
                            fontSize: '0.9rem'
                          }}>
                            Clinical Decision Support (CDS) Findings — {detail.overallValidationSeverity}
                          </strong>
                          <span style={{
                            fontSize: '0.72rem',
                            padding: '2px 8px',
                            borderRadius: '12px',
                            fontWeight: 700,
                            background: detail.overallValidationSeverity === 'CRITICAL' ? '#dc2626' : '#d97706',
                            color: '#ffffff'
                          }}>
                            {detail.validationFindings.length} rule finding(s)
                          </span>
                        </div>
                        <span style={{ fontSize: '0.72rem', color: '#64748b', fontStyle: 'italic' }}>
                          Advisory Only • Pathologist Final Authority
                        </span>
                      </div>

                      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        {detail.validationFindings.map((f, idx) => (
                          <div key={idx} style={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: '8px',
                            padding: '6px 10px',
                            background: '#ffffff',
                            borderRadius: '4px',
                            border: '1px solid #e2e8f0',
                            fontSize: '0.78rem'
                          }}>
                            <span style={{
                              fontWeight: 700,
                              fontSize: '0.7rem',
                              padding: '1px 5px',
                              borderRadius: '3px',
                              background: f.severity === 'CRITICAL' ? '#fee2e2' : '#fef3c7',
                              color: f.severity === 'CRITICAL' ? '#b91c1c' : '#b45309'
                            }}>
                              {f.ruleType}
                            </span>
                            <span style={{ fontWeight: 600, color: '#0f172a' }}>{f.ruleCode}:</span>
                            <span style={{ color: '#334155', flex: 1 }}>{f.findingMessage}</span>
                            {f.currentValue && (
                              <span style={{ color: '#64748b', fontSize: '0.72rem' }}>
                                Val: <strong>{f.currentValue}</strong>{f.percentChange ? ` (Δ ${f.percentChange}%)` : ''}
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="rep-rev-section-title">
                    <span>Observation Parameters & Delta Check Analysis</span>
                    <span style={{ fontSize: '0.75rem', fontWeight: 'normal', color: '#64748b' }}>
                      {detail.observations?.length || 0} parameter(s)
                    </span>
                  </div>

                  <table className="obs-table">
                    <thead>
                      <tr>
                        <th>Parameter Name</th>
                        <th style={{ width: '130px' }}>Origin / Raw</th>
                        <th style={{ width: '110px' }}>Current Value</th>
                        <th style={{ width: '80px' }}>Unit</th>
                        <th style={{ width: '130px' }}>Biological Range</th>
                        <th style={{ width: '90px', textAlign: 'center' }}>Flag</th>
                        <th style={{ width: '180px' }}>Previous History / Delta</th>
                      </tr>
                    </thead>
                    <tbody>
                      {detail.observations && detail.observations.length > 0 ? (
                        detail.observations.map((obs, idx) => (
                          <tr key={idx}>
                            <td style={{ fontWeight: '600' }}>{obs.observationName}</td>
                            <td>
                              {obs.isMachineOrigin ? (
                                <div>
                                  <div style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '4px',
                                    padding: '2px 6px',
                                    borderRadius: '4px',
                                    background: '#eff6ff',
                                    color: '#1d4ed8',
                                    fontWeight: 700,
                                    fontSize: '0.72rem',
                                    border: '1px solid #bfdbfe'
                                  }}
                                  title={`Machine Raw: ${obs.machineRawValue || obs.value}\nNormalized: ${obs.normalizedValue || obs.value}\nAnalyzer: ${obs.analyzerName || obs.analyzerCode || 'Automated'}`}>
                                    <span>🤖</span>
                                    <span>{obs.analyzerCode || 'ANALYZER'}</span>
                                  </div>
                                  {obs.machineRawValue && (
                                    <div style={{ fontSize: '0.68rem', color: '#64748b', marginTop: '2px' }} title="Physical machine reading">
                                      Raw: <strong>{obs.machineRawValue}</strong>
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <span style={{ fontSize: '0.75rem', color: '#64748b' }}>✍️ Manual</span>
                              )}
                            </td>
                            <td style={{ fontWeight: '700', fontSize: '0.88rem' }}>
                              {obs.value ?? '—'}
                            </td>
                            <td style={{ color: '#64748b' }}>{obs.unit || '—'}</td>
                            <td style={{ color: '#475569' }}>{obs.displayRange || '—'}</td>
                            <td style={{ textAlign: 'center' }}>
                              {obs.isCritical ? (
                                <span className="flag-crit">CRIT {obs.flag}</span>
                              ) : obs.flag === 'H' ? (
                                <span className="flag-high">High (H)</span>
                              ) : obs.flag === 'L' ? (
                                <span className="flag-low">Low (L)</span>
                              ) : (
                                <span style={{ color: '#10b981', fontWeight: '600' }}>Normal</span>
                              )}
                            </td>
                            <td>
                              {obs.previousValue ? (
                                <div>
                                  <span style={{ color: '#475569' }}>Prev: {obs.previousValue}</span>{' '}
                                  {obs.deltaChange && (
                                    <span className="delta-badge">{obs.deltaChange}</span>
                                  )}
                                  {obs.deltaAlert && (
                                    <div style={{ color: '#b91c1c', fontSize: '0.72rem', fontWeight: '700' }}>
                                      {obs.deltaAlert}
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <span style={{ color: '#94a3b8', fontStyle: 'italic' }}>No prior records</span>
                              )}
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="7" style={{ textAlign: 'center', padding: '16px', color: '#94a3b8' }}>
                            No observations entered for this test order.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </>
              ) : (
                /* Radiology Review */
                <div style={{ marginBottom: '20px' }}>
                  <div className="rep-rev-section-title">Radiological Findings & Impression</div>
                  <div style={{ background: '#f8fafc', padding: '12px 16px', borderRadius: '6px', marginBottom: '12px' }}>
                    <div style={{ fontSize: '0.75rem', fontWeight: '700', color: '#475569' }}>DETAILED FINDINGS:</div>
                    <div style={{ fontSize: '0.85rem', color: '#0f172a', whiteSpace: 'pre-line', marginTop: '4px' }}>
                      {detail.radiologyReport?.findings || 'No findings recorded.'}
                    </div>
                  </div>
                  <div style={{ background: '#f0f9ff', padding: '12px 16px', borderRadius: '6px', borderLeft: '4px solid #0284c7' }}>
                    <div style={{ fontSize: '0.75rem', fontWeight: '700', color: '#0369a1' }}>DIAGNOSTIC IMPRESSION:</div>
                    <div style={{ fontSize: '0.88rem', fontWeight: '600', color: '#0c4a6e', marginTop: '4px' }}>
                      {detail.radiologyReport?.impression || 'No impression recorded.'}
                    </div>
                  </div>
                </div>
              )}

              {/* Signatory Profile Selection & Verification Block */}
              <div className="rep-rev-sign-config">
                <div style={{ fontSize: '0.85rem', fontWeight: '700', color: '#0f172a', marginBottom: '10px' }}>
                  Reporting Medical Signatory & Digital Signature Snapshot
                </div>
                <div className="sign-config-grid">
                  <div>
                    <label style={{ fontSize: '0.75rem', fontWeight: '600', color: '#475569', display: 'block', marginBottom: '4px' }}>
                      Authorized Reporting Doctor *
                    </label>
                    <select
                      value={selectedDoctorId}
                      onChange={handleDoctorChange}
                      style={{
                        width: '100%',
                        padding: '8px 10px',
                        fontSize: '0.85rem',
                        border: '1px solid #cbd5e1',
                        borderRadius: '6px',
                        background: '#ffffff'
                      }}
                    >
                      {detail.availableSignatures?.map(doc => (
                        <option key={doc.employeeId} value={doc.employeeId}>
                          {doc.doctorName} — {doc.qualification} ({doc.medicalRegNo})
                        </option>
                      ))}
                    </select>

                    <div style={{ marginTop: '10px' }}>
                      <label style={{ fontSize: '0.75rem', fontWeight: '600', color: '#475569', display: 'block', marginBottom: '4px' }}>
                        Release Remarks / Clinical Comments
                      </label>
                      <input
                        type="text"
                        placeholder="e.g. Verified and released under CLSI clinical criteria."
                        value={releaseRemarks}
                        onChange={(e) => setReleaseRemarks(e.target.value)}
                        style={{
                          width: '100%',
                          padding: '7px 10px',
                          fontSize: '0.82rem',
                          border: '1px solid #cbd5e1',
                          borderRadius: '6px'
                        }}
                      />
                    </div>

                    {(detail.isCritical || detail.overallValidationSeverity === 'CRITICAL') && (
                      <div style={{
                        marginTop: '12px',
                        padding: '10px 14px',
                        borderRadius: '6px',
                        background: '#fef2f2',
                        border: '1px solid #fecaca',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px'
                      }}>
                        <input
                          type="checkbox"
                          id="ackCriticalCheck"
                          checked={acknowledgeCritical}
                          onChange={(e) => setAcknowledgeCritical(e.target.checked)}
                          style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                        />
                        <label htmlFor="ackCriticalCheck" style={{ fontSize: '0.82rem', fontWeight: '700', color: '#991b1b', cursor: 'pointer' }}>
                          🚨 Mandatory Pathologist Acknowledgement: I have reviewed the critical panic diagnostic findings and authorize release.
                        </label>
                      </div>
                    )}
                  </div>

                  {/* Stamp & Details Card */}
                  <div>
                    {selectedDoctorProfile ? (
                      <div className="signature-stamp-card">
                        <img
                          src={doctorSignatureService.getSignatureImageUrl(selectedDoctorProfile.profileId)}
                          alt="Doctor Signature"
                          className="stamp-img"
                        />
                        <div>
                          <div style={{ fontWeight: '700', fontSize: '0.88rem', color: '#0f172a' }}>
                            {selectedDoctorProfile.doctorName}
                          </div>
                          <div style={{ fontSize: '0.78rem', color: '#475569' }}>
                            {selectedDoctorProfile.qualification}
                          </div>
                          <div style={{ fontSize: '0.75rem', color: '#0284c7', fontFamily: 'monospace' }}>
                            Reg: {selectedDoctorProfile.medicalRegNo} ({selectedDoctorProfile.regAuthority})
                          </div>
                          <div style={{ fontSize: '0.7rem', color: '#166534', fontWeight: '700', marginTop: '2px' }}>
                            ✓ Active Signature Snapshot v{selectedDoctorProfile.currentVersion}
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div style={{ padding: '16px', background: '#ffffff', border: '1px dashed #cbd5e1', borderRadius: '6px', color: '#94a3b8', fontSize: '0.8rem' }}>
                        No signature profile selected.
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="rep-rev-footer">
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                {detail.approved !== 1 && (
                  <button
                    type="button"
                    className="btn-hold"
                    onClick={() => setShowHoldDialog(true)}
                  >
                    ⏸ Place on Hold
                  </button>
                )}
                {detail.approved === 1 && (
                  <span style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '6px 12px',
                    borderRadius: '6px',
                    background: '#ecfdf5',
                    color: '#065f46',
                    fontWeight: '700',
                    fontSize: '0.82rem',
                    border: '1px solid #a7f3d0'
                  }}>
                    ✓ Officially Approved & Locked
                  </span>
                )}
              </div>

              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  type="button"
                  style={{
                    background: '#f1f5f9',
                    color: '#334155',
                    border: '1px solid #cbd5e1',
                    padding: '8px 16px',
                    borderRadius: '6px',
                    fontWeight: '600',
                    fontSize: '0.85rem',
                    cursor: 'pointer'
                  }}
                  onClick={onClose}
                >
                  Close
                </button>

                {/* Print Final Report Action */}
                <button
                  type="button"
                  style={{
                    background: '#0284c7',
                    color: '#ffffff',
                    border: 'none',
                    padding: '8px 16px',
                    borderRadius: '6px',
                    fontWeight: '700',
                    fontSize: '0.85rem',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    boxShadow: '0 2px 4px rgba(2, 132, 199, 0.3)'
                  }}
                  onClick={() => setIsPrintPreviewOpen(true)}
                >
                  🖨️ Print Final Clinical Report / PDF
                </button>

                {detail.approved !== 1 && (
                  <button
                    type="button"
                    className="btn-release-confirm"
                    onClick={() => setShowConfirmModal(true)}
                    disabled={releasing || (detail.isCritical && !acknowledgeCritical) || !selectedDoctorId}
                  >
                    {releasing ? 'Releasing...' : '✓ Approve & Release Report'}
                  </button>
                )}
              </div>
            </div>
          </>
        )}
      </div>

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div style={{
          position: 'fixed',
          top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          zIndex: 10001,
          display: 'flex', justifyContent: 'center', alignItems: 'center'
        }}>
          <div style={{
            background: '#ffffff',
            borderRadius: '8px',
            padding: '24px',
            width: '440px',
            maxWidth: '90vw',
            boxShadow: '0 20px 25px rgba(0,0,0,0.2)'
          }}>
            <h3 style={{ margin: '0 0 12px 0', fontSize: '1.1rem', color: '#0f172a' }}>
              Confirm Clinical Report Release?
            </h3>
            <p style={{ margin: '0 0 16px 0', fontSize: '0.85rem', color: '#475569', lineHeight: '1.5' }}>
              You are about to approve and lock the report for <strong>{detail?.patientName}</strong> ({detail?.ledgerTransactionNo}).
              <br /><br />
              <strong>Reporting Doctor:</strong> {selectedDoctorProfile?.doctorName}<br />
              <strong>Signature Version:</strong> v{selectedDoctorProfile?.currentVersion}<br />
              <strong>Medical Registration:</strong> {selectedDoctorProfile?.medicalRegNo}
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button
                type="button"
                style={{
                  background: '#f1f5f9',
                  color: '#334155',
                  border: '1px solid #cbd5e1',
                  padding: '8px 14px',
                  borderRadius: '6px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
                onClick={() => setShowConfirmModal(false)}
              >
                Back to Review
              </button>
              <button
                type="button"
                style={{
                  background: '#059669',
                  color: '#ffffff',
                  border: 'none',
                  padding: '8px 18px',
                  borderRadius: '6px',
                  fontWeight: '700',
                  cursor: 'pointer'
                }}
                onClick={handleConfirmRelease}
                disabled={releasing}
              >
                {releasing ? 'Releasing...' : 'Confirm & Release Report'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Hold Reason Dialog */}
      {showHoldDialog && (
        <div style={{
          position: 'fixed',
          top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          zIndex: 10001,
          display: 'flex', justifyContent: 'center', alignItems: 'center'
        }}>
          <div style={{
            background: '#ffffff',
            borderRadius: '8px',
            padding: '20px',
            width: '420px',
            maxWidth: '90vw'
          }}>
            <h3 style={{ margin: '0 0 10px 0', fontSize: '1rem', color: '#991b1b' }}>
              Place Test Order on Clinical Hold
            </h3>
            <label style={{ fontSize: '0.78rem', color: '#475569', display: 'block', marginBottom: '4px' }}>
              Reason for Hold (e.g. Hemolyzed sample, rerun requested, clinician correlation required) *
            </label>
            <textarea
              rows="3"
              style={{
                width: '100%',
                padding: '8px',
                fontSize: '0.85rem',
                border: '1px solid #cbd5e1',
                borderRadius: '6px',
                marginBottom: '14px',
                boxSizing: 'border-box'
              }}
              value={holdReason}
              onChange={(e) => setHoldReason(e.target.value)}
              placeholder="Enter clinical reason..."
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
              <button
                type="button"
                style={{
                  background: '#f1f5f9',
                  color: '#334155',
                  border: '1px solid #cbd5e1',
                  padding: '6px 12px',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
                onClick={() => setShowHoldDialog(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                style={{
                  background: '#ef4444',
                  color: '#ffffff',
                  border: 'none',
                  padding: '6px 14px',
                  borderRadius: '4px',
                  fontWeight: '700',
                  cursor: 'pointer'
                }}
                onClick={handleConfirmHold}
                disabled={holding}
              >
                {holding ? 'Saving...' : 'Confirm Hold'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* High-Fidelity Diagnostic Print & PDF Preview Modal */}
      {isPrintPreviewOpen && detail && (
        <PathologyReportPreviewModal
          isOpen={isPrintPreviewOpen}
          onClose={() => setIsPrintPreviewOpen(false)}
          patient={{
            patientName: detail.patientName,
            patientId: detail.patientId,
            ledgerTransactionNo: detail.ledgerTransactionNo,
            ageGender: detail.ageGender,
            barcodeNo: detail.barcodeNo,
            doctorName: detail.referringDoctor,
            centreName: detail.centreName,
            sampleReceiveDate: detail.sampleReceiveDate,
            approvedDate: detail.approvedDate,
            resultEnteredBy: detail.resultEnteredBy,
            reportingDoctorId: selectedDoctorProfile?.employeeId || detail.reportingDoctorId,
            reportingDoctorName: selectedDoctorProfile?.doctorName || detail.reportingDoctorName,
            signatureProfileId: selectedDoctorProfile?.profileId || detail.signatureProfileId,
            signatureVersion: selectedDoctorProfile?.currentVersion || detail.signatureVersion,
            approved: detail.approved,
            approvedBy: detail.approvedBy
          }}
          tests={[{
            testId: detail.testId,
            investigationName: detail.itemName,
            departmentName: detail.department,
            approved: detail.approved,
            reportingDoctorName: selectedDoctorProfile?.doctorName || detail.reportingDoctorName,
            signatureProfileId: selectedDoctorProfile?.profileId || detail.signatureProfileId,
            isOutsourced: detail.isOutsourced,
            externalResultSummary: detail.externalResultSummary,
            externalAttachmentUrl: detail.externalAttachmentUrl,
            observations: detail.observations?.map(o => ({
              name: o.observationName,
              currentValue: o.value,
              value: o.value,
              unit: o.unit,
              minReading: o.minReading,
              maxReading: o.maxReading,
              displayReading: o.displayRange,
              flag: o.flag,
              isCritical: o.isCritical
            }))
          }]}
          signatureProfile={selectedDoctorProfile || detail.selectedSignatureProfile}
        />
      )}
    </div>
  );
};

export default ReportReviewModal;
